import { createPrimitiveField } from 'src/util/data-entry-ui/create-form-inputs/createPrimitiveField';
import { EditableTournament } from '../toFormInput';
import {
  getBlurb,
  getEndpoints,
  getPayouts,
  getPrize,
  getQualification,
  getRegistrationOptions,
  getSponsors,
  getTournamentDetails,
} from './getField';
import { createObjectField } from 'src/util/data-entry-ui/create-form-inputs/createObjectField';

export const getTournament = (tournamentInput: EditableTournament) => {
  const tournamentPrimitiveFields = [
    {
      fieldName: 'title',
      value: tournamentInput.title,
      required: true,
      regexExplained:
        'Please enter a tournament title containing up to 50 characters, which can include letters, numbers, underscores, and spaces. The name should not start with a date in the format YYYY-MM-DD or a time in the format Thh:mm.',
      regex: /^(?!\d{4}-\d{2}-\d{2})(?!T\d{2}:\d{2})([\w\s]{0,50})$/,
    },
    {
      fieldName: 'date',
      value: new Date(),
      label: 'Start Date & Time',
      required: true,
      regexExplained:
        'Please enter a valid start date and time. Use the date picker provided by the browser to ensure proper formatting.',
    },
    {
      fieldName: 'endDate',
      value: new Date(),
      label: 'End Date & Time',
      required: true,
      regexExplained:
        'Please enter a valid end date and time. Use the date picker provided by the browser to ensure proper formatting.',
    },
    {
      fieldName: 'checkInTime',
      value: new Date(),
      label: 'Check In Date & Time',
      required: true,
      regexExplained:
        'Please enter a valid check-in date and time. Use the date picker provided by the browser to ensure proper formatting.',
    },
    {
      fieldName: 'readyTime',
      value: new Date(),
      label: 'Ready Date & Time',
      required: true,
      regexExplained:
        'Please enter a valid ready date and time. Use the date picker provided by the browser to ensure proper formatting.',
    },
    {
      fieldName: 'imgUrl',
      value: tournamentInput.imgUrl,
      label: 'Image URL',
      required: true,
      regexExplained:
        'Please enter a valid image url hosted on Firebase Storage. The url should start with "https://firebasestorage.googleapis.com/v0/b/blumint-".',
      regex:
        /^https:\/\/firebasestorage\.googleapis\.com\/v0\/b\/blumint-(development|production)\.appspot\.com\/o\/Game%/,
    },
    {
      fieldName: 'profileImgUrl',
      value: tournamentInput.profileImgUrl,
      label: 'Profile Image URL',
      required: true,
      regexExplained:
        'Please enter a valid image url hosted on Firebase Storage. The url should start with "https://firebasestorage.googleapis.com/v0/b/blumint-".',
      regex:
        /^https:\/\/firebasestorage\.googleapis\.com\/v0\/b\/blumint-(development|production)\.appspot\.com\/o\/Game%/,
    },
    {
      fieldName: 'gameId',
      value: tournamentInput.gameId,
      required: true,
      regexExplained:
        'Please enter a valid game ID consisting of lowercase letters and hyphens, e.g. "smash-bros" or "battle-royale".',
      // eslint-disable-next-line security/detect-unsafe-regex
      regex: /^[a-z]+(?:-[a-z]+)*$/,
    },
    {
      fieldName: 'gameTitle',
      value: tournamentInput.gameTitle,
      required: true,
      regexExplained:
        'Please enter a valid game title containing 1 to 50 characters. Allowed characters include letters, numbers, spaces, colons (:), periods (.), hyphens (-), and parentheses (()).',
      regex: /^[\w\s:.()-]{1,50}$/,
    },
    {
      fieldName: 'maxTeamSize',
      value: tournamentInput.maxTeamSize,
      label: 'Max Team Size',
      regex: /^(?:[1-9]|[1-9]\d+)$/,
      regexExplained:
        'Please input a valid Team Size. It must be a positive integer greater than or equal to 1.',
    },
    {
      fieldName: 'maxTeamCount',
      value: tournamentInput.maxTeamCount,
      label: 'Max Team Count',
      regex: /^(1000|[2-9]|[1-9][0-9]|[1-9][0-9]{2}|10(0[1-9]|1[0-9]|2[0-4]))$/,
      regexExplained:
        'Please input a valid Team Size, it has to be between 2 and 1024.',
    },
    {
      fieldName: 'bracketType',
      value: tournamentInput.bracketType,
      label: 'Bracket Type',
      regexExplained:
        'Please enter a valid bracket type. You can input either single-elimination, double-elimination, heats, or no-bracket.',
      regex: /^(single-elimination|double-elimination|heats|no-bracket)$/,
    },
    {
      fieldName: 'skipCheckIn',
      value: !!tournamentInput?.skipCheckIn,
    },
    {
      fieldName: 'maxMatchLength',
      value: tournamentInput.maxMatchLength,
      regexExplained:
        'Please enter a valid max match length (in milliseconds). The minimum value is 60000 ms (1 minute), there is no upper limit.',
      regex: /^([6-9]\d{3}|\d{5,})$/,
    },
    {
      fieldName: 'maxMatchDelay',
      value: tournamentInput.maxMatchDelay,
      regexExplained:
        'Please enter a valid delay for each ongoing match (in milliseconds). The minimum value is 60000 ms (1 minute), there is no upper limit.',
      regex: /^([6-9]\d{3}|\d{5,})$/,
    },
    {
      fieldName: 'bufferTime',
      value: tournamentInput.bufferTime,
      label: 'Buffer Time',
      regexExplained:
        'Please enter a valid buffer time between matches (in milliseconds). The minimum value is 60000 ms (1 minute), there is no upper limit.',
      regex: /^([6-9]\d{3}|\d{5,})$/,
    },
    {
      fieldName: 'concurrentMatches',
      value: tournamentInput.concurrentMatches,
      regexExplained:
        'Please enter a valid number of concurrent matches. The maximum number of matches that can be held at the same time is 512 as the maximum number of teams we can accommodate is 1024.',
      regex: /^(0|[1-9]\d{0,5}|512)$/,
    },
    {
      fieldName: 'roundGap',
      value: tournamentInput.roundGap,
      regexExplained:
        'Please enter a valid time between rounds (in milliseconds). The minimum value is 60000 ms (1 minute), there is no upper limit.',
      regex: /^([6-9]\d{3}|\d{5,})$/,
    },
    {
      fieldName: 'region',
      value: tournamentInput.region ? tournamentInput.region : 'US-West',
      regexExplained:
        'Please enter a valid region containing only letters, spaces, or hyphens. If left empty, this will be set to US-West.',
      regex: /^[A-Za-z\s-]*$/,
    },
  ];
  const tournamentFields = [
    ...tournamentPrimitiveFields.map(createPrimitiveField),
    getPrize(tournamentInput.prize),
    getBlurb(tournamentInput.blurb),
    getRegistrationOptions(tournamentInput.registrationOptions),
    getEndpoints(tournamentInput.endpoints),
    getPayouts(tournamentInput.payouts),
    getTournamentDetails(tournamentInput.tournamentDetails),
    getQualification(tournamentInput.qualification),
    getSponsors(tournamentInput.sponsors),
  ];
  const tournament = createObjectField<EditableTournament>(
    tournamentFields,
    'Create New Tournament',
  );
  return tournament;
};
